import React, { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { api, apiErr, API } from "@/lib/api";
import { FileText, Eraser, CheckCircle2, Loader2, Download, Printer, PenLine, MessageCircle, BadgeCheck, CalendarDays, ShieldCheck, Building2 } from "lucide-react";
import { WHATSAPP_LINK } from "@/data/constants";

const DOC_DESC = {
  compromiso: "Tu compromiso de viaje y presentación profesional: tus datos, el mes elegido, el desglose de costos y la modalidad de pago que seleccionaste.",
  clausula: "Cláusula de responsabilidad económica en caso de cancelación del vuelo, conforme a la legislación mexicana.",
  promesa: "Promesa de contratación (carta de invitación de Expander Work) con tu puesto, salario, jornada y alojamiento en Polonia.",
};

function SignaturePad({ onChange }) {
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  useEffect(() => {
    const ctx = canvasRef.current.getContext("2d");
    ctx.lineWidth = 2.6; ctx.lineCap = "round"; ctx.lineJoin = "round"; ctx.strokeStyle = "#0B132B";
  }, []);
  const pos = (e) => {
    const c = canvasRef.current; const r = c.getBoundingClientRect();
    const t = e.touches ? e.touches[0] : e;
    return { x: (t.clientX - r.left) * (c.width / r.width), y: (t.clientY - r.top) * (c.height / r.height) };
  };
  const start = (e) => { e.preventDefault(); drawing.current = true; const ctx = canvasRef.current.getContext("2d"); const p = pos(e); ctx.beginPath(); ctx.moveTo(p.x, p.y); };
  const move = (e) => { if (!drawing.current) return; e.preventDefault(); const ctx = canvasRef.current.getContext("2d"); const p = pos(e); ctx.lineTo(p.x, p.y); ctx.stroke(); };
  const end = () => { if (drawing.current) { drawing.current = false; onChange(canvasRef.current.toDataURL("image/png")); } };
  const clear = () => { const c = canvasRef.current; c.getContext("2d").clearRect(0, 0, c.width, c.height); onChange(null); };
  return (
    <div>
      <div className="rounded-xl border-2 border-dashed border-[#253252] bg-white overflow-hidden">
        <canvas ref={canvasRef} width={640} height={180} className="w-full touch-none cursor-crosshair block" style={{ height: "180px" }}
          onMouseDown={start} onMouseMove={move} onMouseUp={end} onMouseLeave={end}
          onTouchStart={start} onTouchMove={move} onTouchEnd={end} data-testid="signature-canvas" />
      </div>
      <div className="flex justify-between mt-2">
        <span className="text-xs text-slate-400">Dibuja tu firma con el dedo o el mouse.</span>
        <button onClick={clear} className="flex items-center gap-1.5 text-sm text-slate-300 hover:text-[#E63946]"><Eraser size={15} /> Borrar</button>
      </div>
    </div>
  );
}

function DocCard({ token, doc, index, onSigned }) {
  const [sig, setSig] = useState(null);
  const [busy, setBusy] = useState(false);
  const pdfUrl = `${API}/agreements/${token}/pdf/${doc.key}?ts=${doc.signed_at ? new Date(doc.signed_at).getTime() : Date.now()}`;
  const submit = async () => {
    if (!sig) { toast.error("Primero dibuja tu firma"); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/agreements/${token}/sign/${doc.key}`, { signature: sig });
      toast.success("Documento firmado");
      onSigned(data.all_signed);
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };
  return (
    <div className="bg-[#1C2541] rounded-2xl border border-white/10 p-5" data-testid={`doc-card-${doc.key}`}>
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="text-xs text-[#FFD166] font-semibold">Documento {index + 1} de 3</div>
          <div className="font-head font-bold text-white mt-0.5">{doc.title}</div>
        </div>
        {doc.signed && <span className="text-xs bg-[#16a34a]/20 text-[#5eead4] px-2.5 py-1 rounded-full font-semibold flex items-center gap-1"><CheckCircle2 size={13} /> Firmado</span>}
      </div>
      <a href={pdfUrl} target="_blank" rel="noreferrer" data-testid={`view-pdf-${doc.key}`}
        className="mt-3 flex items-center justify-center gap-2 bg-[#253252] text-white text-sm font-semibold px-4 py-2.5 rounded-xl hover:bg-[#2f3d5e]">
        <FileText size={16} /> Ver documento (PDF)
      </a>
      {doc.signed ? (
        <a href={pdfUrl} target="_blank" rel="noreferrer" data-testid={`download-${doc.key}`}
          className="mt-2 flex items-center justify-center gap-2 bg-[#FFD166] text-[#0B132B] text-sm font-bold px-4 py-2.5 rounded-xl hover:bg-[#E0A926]">
          <Download size={16} /> Descargar firmado
        </a>
      ) : (
        <div className="mt-3">
          <SignaturePad onChange={setSig} />
          <button onClick={submit} disabled={busy} data-testid={`sign-btn-${doc.key}`}
            className="w-full mt-3 flex items-center justify-center gap-2 bg-[#FFD166] text-[#0B132B] font-bold px-5 py-3 rounded-full hover:bg-[#E0A926] disabled:opacity-60">
            {busy ? <Loader2 className="animate-spin" size={17} /> : <CheckCircle2 size={17} />} Firmar este documento
          </button>
        </div>
      )}
    </div>
  );
}

export default function SignAgreement() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [mode, setMode] = useState(null);

  const load = async () => {
    setLoading(true);
    try { const res = await api.get(`/agreements/${token}/public`); setData(res.data); }
    catch (e) { setNotFound(true); } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [token]); // eslint-disable-line

  if (loading) return <div className="min-h-screen bg-[#0B132B] flex items-center justify-center"><Loader2 className="animate-spin text-[#FFD166]" size={32} /></div>;
  if (notFound) return <div className="min-h-screen bg-[#0B132B] flex items-center justify-center px-4"><div className="text-center"><FileText className="mx-auto text-slate-600" size={40} /><p className="text-slate-300 mt-3">Este enlace no existe o es inválido.</p></div></div>;

  return (
    <div className="min-h-screen bg-[#0B132B] text-white" data-testid="sign-agreement-page">
      <header className="border-b border-white/10 py-4">
        <div className="max-w-2xl mx-auto px-4 flex justify-center">
          <span className="text-xl font-head font-extrabold">europe<span className="text-[#FFD166]">jobs</span> <span className="text-sm text-slate-400 font-normal">· JE MEXICO</span></span>
        </div>
      </header>
      <div className="max-w-2xl mx-auto px-4 py-8 space-y-5">
        <div>
          <span className="text-[#FFD166] text-sm font-semibold uppercase tracking-wider">{data.all_signed ? "Etapa 4 · Documentos firmados" : "Etapa 3 · Firma"}</span>
          <h1 className="font-head text-2xl sm:text-3xl font-extrabold mt-1">{data.all_signed ? "¡Todo listo! Aquí están tus documentos" : "Firma tus documentos"}</h1>
          <p className="text-slate-400 mt-1">Hola <strong className="text-white">{data.candidate_name || "candidato/a"}</strong>{data.all_signed ? ", gracias por completar tu compromiso con nosotros." : ", revisa el resumen de tus 3 documentos y fírmalos."}</p>
        </div>

        {!data.all_signed && (
          <div className="bg-[#12203f] border border-white/10 rounded-2xl p-5" data-testid="docs-summary">
            <div className="flex items-center gap-2 text-[#FFD166] font-head font-bold text-sm"><FileText size={16} /> Resumen de tus documentos</div>
            <div className="mt-3 space-y-2.5">
              {data.docs.map((doc, i) => (
                <div key={doc.key} className="flex gap-3 bg-[#0d1730] rounded-xl p-3 border border-white/5" data-testid={`summary-${doc.key}`}>
                  <span className="w-7 h-7 rounded-full bg-[#FFD166] text-[#0B132B] font-extrabold text-sm flex items-center justify-center shrink-0">{i + 1}</span>
                  <div>
                    <div className="font-semibold text-white text-sm flex items-center gap-2">{doc.title}
                      {doc.signed && <CheckCircle2 size={14} className="text-[#22C55E]" />}
                    </div>
                    <div className="text-xs text-slate-400 mt-0.5">{DOC_DESC[doc.key] || ""}</div>
                  </div>
                </div>
              ))}
            </div>
            <p className="text-xs text-slate-500 mt-3 flex items-start gap-1.5"><ShieldCheck size={13} className="shrink-0 mt-0.5" /> Léelos con calma. Al firmar los 3, la empresa te los devolverá firmados por ambas partes.</p>
          </div>
        )}

        {data.all_signed ? (
          <div className="space-y-4" data-testid="all-signed-box">
            <div className="bg-gradient-to-br from-[#16a34a]/25 to-[#0d1730] border border-[#22C55E]/40 rounded-2xl p-6 text-center">
              <div className="w-14 h-14 mx-auto rounded-2xl bg-[#22C55E]/20 flex items-center justify-center"><BadgeCheck className="text-[#22C55E]" size={30} /></div>
              <p className="font-head font-extrabold text-xl mt-3">¡Firmaste tu compromiso! 🎉</p>
              <p className="text-slate-300 text-sm mt-2 max-w-md mx-auto">
                Tus <strong className="text-white">3 documentos</strong> quedaron firmados <strong className="text-white">por ti y por la empresa</strong> (EuropeJobs / JE MEXICO y Expander Work). Descárgalos y guárdalos: son tu copia oficial.
              </p>
            </div>

            <div className="bg-[#12203f] border border-white/10 rounded-2xl p-5">
              <div className="flex items-center gap-2 text-[#FFD166] font-head font-bold text-sm"><Download size={16} /> Descarga tus documentos firmados</div>
              <div className="mt-3 space-y-2">
                {data.docs.map((doc, i) => (
                  <a key={doc.key} href={`${API}/agreements/${token}/pdf/${doc.key}?ts=${doc.signed_at ? new Date(doc.signed_at).getTime() : Date.now()}`} target="_blank" rel="noreferrer" data-testid={`final-dl-${doc.key}`}
                    className="flex items-center justify-between gap-2 bg-[#253252] text-white text-sm font-semibold px-4 py-3 rounded-xl hover:bg-[#2f3d5e] transition-colors">
                    <span className="flex items-center gap-2.5"><span className="w-6 h-6 rounded-full bg-[#FFD166] text-[#0B132B] font-extrabold text-xs flex items-center justify-center shrink-0">{i + 1}</span> {doc.title}</span>
                    <span className="flex items-center gap-1.5 text-[#FFD166]"><CheckCircle2 size={14} /> <Download size={16} /></span>
                  </a>
                ))}
              </div>
            </div>

            <div className="bg-[#12203f] border border-[#FFD166]/30 rounded-2xl p-5 flex gap-4">
              <span className="w-11 h-11 rounded-xl bg-[#FFD166]/15 flex items-center justify-center text-[#FFD166] shrink-0"><CalendarDays size={22} /></span>
              <div>
                <div className="font-head font-bold text-white">Un agente se pondrá en contacto contigo</div>
                <p className="text-sm text-slate-300 mt-1">
                  Un asesor te contactará para <strong className="text-white">coordinar tu vuelo en el mes de {data.travel_month || "el mes que elegiste"}</strong>: te confirmará el día exacto de salida, el aeropuerto y todos los detalles. ¡Prepárate para tu nueva aventura en Polonia! 🇵🇱
                </p>
                <a href={WHATSAPP_LINK} target="_blank" rel="noreferrer" data-testid="stage4-whatsapp"
                  className="inline-flex items-center gap-2 mt-3 bg-[#25D366] text-white font-bold px-5 py-2.5 rounded-full text-sm">
                  <MessageCircle size={16} /> Escríbenos por WhatsApp
                </a>
              </div>
            </div>
            <p className="text-xs text-slate-500 text-center flex items-center justify-center gap-1.5"><Building2 size={13} /> EuropeJobs · JE MEXICO · Expander Work Sp. z o.o.</p>
          </div>
        ) : !mode ? (
          <div className="grid sm:grid-cols-2 gap-4" data-testid="mode-choice">
            <button onClick={() => setMode("web")} data-testid="mode-web"
              className="text-left bg-[#1C2541] border border-white/10 rounded-2xl p-5 hover:border-[#FFD166]/50 transition-colors">
              <PenLine className="text-[#FFD166]" size={24} />
              <div className="font-head font-bold text-white mt-2">Firmar por la web</div>
              <div className="text-sm text-slate-400 mt-1">Firma cada documento aquí mismo, desde tu teléfono o computadora.</div>
            </button>
            <button onClick={() => { setMode("print"); api.post(`/agreements/${token}/method`, { method: "whatsapp" }).catch(() => {}); }} data-testid="mode-print"
              className="text-left bg-[#1C2541] border border-white/10 rounded-2xl p-5 hover:border-[#FFD166]/50 transition-colors">
              <Printer className="text-[#FFD166]" size={24} />
              <div className="font-head font-bold text-white mt-2">Imprimir y enviar por WhatsApp</div>
              <div className="text-sm text-slate-400 mt-1">Descarga los 3 documentos, imprímelos, fírmalos a mano y envíalos a un asesor.</div>
            </button>
          </div>
        ) : mode === "print" ? (
          <div className="space-y-4" data-testid="print-mode">
            <button onClick={() => setMode(null)} className="text-sm text-slate-400 hover:text-white">← Cambiar método</button>
            <div className="bg-[#1C2541] border border-white/10 rounded-2xl p-5 space-y-3">
              <p className="text-sm text-slate-300">Descarga, imprime y firma a mano los 3 documentos. Luego envía una foto o escaneo a tu asesor por WhatsApp.</p>
              {data.docs.map((doc) => (
                <a key={doc.key} href={`${API}/agreements/${token}/pdf/${doc.key}?ts=${doc.signed_at ? new Date(doc.signed_at).getTime() : Date.now()}`} target="_blank" rel="noreferrer" data-testid={`print-dl-${doc.key}`}
                  className="flex items-center justify-between gap-2 bg-[#253252] text-white text-sm font-semibold px-4 py-3 rounded-xl hover:bg-[#2f3d5e]">
                  <span className="flex items-center gap-2"><FileText size={16} /> {doc.title}</span> <Download size={16} />
                </a>
              ))}
              <a href={WHATSAPP_LINK} target="_blank" rel="noreferrer" data-testid="print-whatsapp"
                className="flex items-center justify-center gap-2 bg-[#25D366] text-white font-bold px-5 py-3 rounded-full mt-2">
                <MessageCircle size={18} /> Enviar mis documentos a un asesor
              </a>
            </div>
          </div>
        ) : (
          <div className="space-y-5" data-testid="web-mode">
            <button onClick={() => setMode(null)} className="text-sm text-slate-400 hover:text-white">← Cambiar método</button>
            {data.docs.map((doc, i) => (
              <DocCard key={doc.key} token={token} doc={doc} index={i} onSigned={() => load()} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
